import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { HomeView } from './HomeView';
import { PaywallModal } from './PaywallModal';
import { QuoteEntryPicker } from './QuoteEntryPicker';
import { UnavailableModal } from './UnavailableModal';
import { HOME_BLOCKS } from './homeBlocks';
import type { HomeBlock } from './types';
import type { CalculatorDraft } from './DraftBanner';
import { useAuthStore } from '@/lib/stores/authStore';
import { usePaywallStatus } from '@/hooks/usePaywallStatus';

/**
 * Текст confirm-диалога logout — фиксированный, согласован Aibek в approve №1
 * (Q5). Сохраняем lang/city, чистим jwt+profile+consents+subscription
 * (см. authStore.logout). После logout `navigate('/')` — App.tsx сам редиректнет
 * на /onboarding/lang-city только если lang/city очистились. По текущей политике
 * (keep lang/city) — пользователь остаётся на Главной в guest mode.
 */
const LOGOUT_CONFIRM_MESSAGE =
  'Выйти из приложения? Ваши локальные настройки (язык, город) будут сохранены, ' +
  'но придётся пройти регистрацию заново для платных функций.';

/**
 * Container Главной (Экран 2.0, Block 0). Связывает локальный state, навигацию,
 * subscription state из authStore и stub-handlers с pure-presenter `HomeView`.
 *
 * ── Phase 4 (ADR-0014) + PR-G Phase 7.2 — subscription-aware paywall logic ──
 *
 * `handleBlockClick` на блок с `paywallGate === true` (prdBlockNumber 12/13)
 * делегирует access decision на `usePaywallStatus()` (single source of truth,
 * shared с route-level `<PaywallGuard/>`). Логика:
 *  - status === 'allowed' → navigate на real route:
 *      prdBlockNumber=12 → `/calculator` (Block 12 PR-A);
 *      prdBlockNumber=13 → `/contracts` (Block 13 PR-G Phase 6).
 *  - status === 'blocked' → PaywallModal (existing UI preserved).
 *
 * `usePaywallStatus` инкапсулирует matrix:
 *  - TEST_DEPLOY_BYPASS=true → allowed (production safety bypass).
 *  - FEATURE_FLAGS.PAYWALL_ENABLED=false → blocked (kill-switch).
 *  - subscription === null → blocked (defensive: mount-fetch race / guest).
 *  - subscription.status ∈ {trial, paid} → allowed.
 *  - subscription.status ∈ {grace, expired_readonly, archived} → blocked.
 *
 * Trial banner показывается ТОЛЬКО при `subscription.status === 'trial'`
 * (HomeView). Дни считаются через `getDaysRemaining()` — recalculate at render
 * (без polling, ADR-0014 Q4 stale-but-acceptable).
 *
 * Phase 1/2/3 политики сохраняются:
 * - `paywallGate=false && available=false` → UnavailableModal (10 блоков).
 * - `paywallGate=false && available=true` → navigate(href) (Декор/Краска/Ручки).
 * - `draft` опциональный prop для тестов и Phase 9+ MSW
 *   `GET /api/calculator/projects?status=draft`.
 *
 * `onTrialClick` / `onSubscribeClick` / `onPay` (PaywallModal) — alert-stubs
 * Phase 4 (ADR-0014 Q7-Q8). Real /pricing redirect — Phase Block 11 Экран 2.11.3.
 */
export interface HomeProps {
  /**
   * Опционально для тестов и будущей Phase 4. По умолчанию undefined — баннер
   * «Продолжить расчёт» не рендерится. В Phase 4 Container будет фетчить
   * /api/calculator/projects?status=draft через MSW + Zod-схему.
   */
  draft?: CalculatorDraft;
}

export function Home({ draft }: HomeProps = {}) {
  const navigate = useNavigate();
  const logout = useAuthStore((s) => s.logout);
  const subscription = useAuthStore((s) => s.subscription);
  const getDaysRemaining = useAuthStore((s) => s.getDaysRemaining);
  const paywallStatus = usePaywallStatus();
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [unavailableOpen, setUnavailableOpen] = useState(false);
  // PR-H5a: picker «Коммерческое предложение» (id=14, entry='quote'). Открывается
  // ТОЛЬКО после прохождения paywall-гейта (paywallStatus === 'allowed').
  const [quotePickerOpen, setQuotePickerOpen] = useState(false);

  const handleBlockClick = (block: HomeBlock) => {
    if (block.paywallGate) {
      // PR-G Phase 7.2 — paywall decision delegated to `usePaywallStatus()`,
      // shared с route-level `<PaywallGuard/>`. Matrix logic — см. hook.
      // Phase 7 hotfix: на 'pending' (subscription===null + jwt!==null —
      // mount-fetch in-flight) ведём себя defensively как при 'blocked' →
      // открываем PaywallModal. В практике пользователь не увидит pending на
      // Home (App.tsx fetch'ит /api/me на mount '/'), но на race-окне сразу
      // после refresh это возможный transient state.
      if (paywallStatus !== 'allowed') {
        setPaywallOpen(true);
        return;
      }

      // Block 12 — real route после Phase A6 (закрыт B-052 / ADR-0014 §Q5).
      // Block 13 — real route после PR-G Phase 6 (закрыт B-116) — entry-screen
      // «Мои договоры» c empty-state CTA «+ Создать договор».
      if (block.prdBlockNumber === 12) {
        // PR-H5a: два входа в Block 12 различаются дискриминатором `entry`:
        //   entry='quote' (id=14 «Коммерческое предложение») → QuoteEntryPicker;
        //   entry отсутствует (id=8 «Рассчитать стоимость») → /calculator (default).
        if (block.entry === 'quote') {
          setQuotePickerOpen(true);
          return;
        }
        navigate('/calculator');
        return;
      }
      if (block.prdBlockNumber === 13) {
        navigate('/contracts');
        return;
      }
      // Defensive: paywallGate=true для блока вне 12/13 — сейчас невозможно
      // (homeBlocks.ts фиксирует gate только для 12/13), но если в будущем
      // расширят — fallback на PaywallModal безопаснее чем silent navigate.
      setPaywallOpen(true);
      return;
    }
    if (!block.available) {
      setUnavailableOpen(true);
      return;
    }
    if (block.href) {
      navigate(block.href);
    }
  };

  const handleLogout = () => {
    if (!window.confirm(LOGOUT_CONFIRM_MESSAGE)) return;
    logout();
    navigate('/');
  };

  // Trial banner: видим только при `status === 'trial'`. `daysRemaining`
  // рассчитывается через store helper getDaysRemaining (recalculate at render,
  // ADR-0014 Q4). null subscription / non-trial state → showTrial=false.
  const showTrial = subscription?.status === 'trial';
  const trialDays = showTrial ? (getDaysRemaining() ?? 0) : 0;

  return (
    <>
      <HomeView
        blocks={HOME_BLOCKS}
        showTrial={showTrial}
        trialDays={trialDays}
        draft={draft}
        onTrialClick={() =>
          alert(
            'Phase Block 11 — 2.11.3 «Купи подписку со скидкой 30%». Real /pricing — будущая фаза.',
          )
        }
        onDraftClick={(d) => alert(`Phase 4: открыть калькулятор ${d.screen} с draft "${d.name}"`)}
        onBlockClick={handleBlockClick}
        onSubscribeClick={() =>
          alert('Phase Block 11 — 2.11.3 «Подписка / оплата». Real /pricing — будущая фаза.')
        }
        onLogout={handleLogout}
      />
      {paywallOpen && (
        <PaywallModal
          onPay={() => {
            setPaywallOpen(false);
            alert('Phase Block 11 — 2.11.3 «Подписка / оплата». Real /pricing — будущая фаза.');
          }}
          onClose={() => setPaywallOpen(false)}
        />
      )}
      {unavailableOpen && <UnavailableModal onClose={() => setUnavailableOpen(false)} />}
      {quotePickerOpen && (
        <QuoteEntryPicker
          onCreateNew={() => {
            setQuotePickerOpen(false);
            navigate('/calculator/quote?new=true');
          }}
          onOpenSaved={() => {
            setQuotePickerOpen(false);
            navigate('/calculator/quote/saved');
          }}
          onClose={() => setQuotePickerOpen(false)}
        />
      )}
    </>
  );
}
